import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Device } from '../../../../class/Device';
import { DEVICES_UNIT_DEVICE } from '../../../../component/devices/devices-routing.constants';
import { DevicesManagerService } from '../../../../services/devices-manager.service';
import DriversService from '../../../../services/drivers.service';
import { KeywordStateService } from '../../../../services/keyword-state.service';
import LandmarksService from '../../../../services/landmarks.service';
import { LoadingService } from '../../../../services/loading.service';
import { MapService } from '../../../../services/map.service';
import { RoutingService } from '../../../../services/routing.service';
import UnitsService from '../../../../services/units.service';
import { ViewService } from '../../../../services/view.service';
import DurationPipe from '../../../../utils/pipes/duration.pipe';
import DynamicDatePipe from '../../../../utils/pipes/dynamic-date.pipe';
import ImperialPipe from '../../../../utils/pipes/imperial.pipe';
import { NavigationCardComponent } from '../navigation-card/navigation-card.component';

@Component({
  selector: 'device-card',
  templateUrl: './device-card.component.html',
  styleUrls: ['./device-card.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    DurationPipe,
    ImperialPipe,
    TranslatePipe,
    MatTooltipModule,
    DynamicDatePipe,
    MatIconModule,
    NavigationCardComponent,
  ],
})
export class DeviceCardComponent implements OnInit, OnDestroy {
  private _unitsService = inject(UnitsService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _landmarksService = inject(LandmarksService);
  public $landmarksById = this._landmarksService.$landmarksById.asReadonly();
  private _keywordStateService = inject(KeywordStateService);
  public $keywords = this._keywordStateService.$keywords;
  private _driversService = inject(DriversService);
  public mapService = inject(MapService);
  public router = inject(Router);
  public routingService = inject(RoutingService);
  public loadingService = inject(LoadingService);
  private _viewService = inject(ViewService<string>);

  @Input() d: number;
  @Input() display:
    'realDistance' | 'triplessTime' | 'private' | 'distance' | 'keywords' | 'error' | 'locked' | 'beacon';
  @Input() time?: number;
  @Input() distance: number | undefined;
  @Input() isDisplayDriver = true;
  public goToUrl: string;
  private servicesLoadedEventSubscriber: Subscription;
  private poisLoadedEventSubscriber: Subscription;
  public device: Device;
  public $drivers = this._driversService.$valuesRecord;
  public $devicesMap = this._devicesManagerService.$devicesMap;

  ngOnInit() {
    if (this.time && this.display === 'triplessTime') {
      this.time = this.time / (86400 * 1000);
      this.time = Number(this.time.toFixed(0));
    } else if (this.time && this.display === 'private') {
      this.time = Math.round(Date.now() - this.time * 1000);
    }
    if (this._unitsService.$unitsById() && this.$landmarksById()) {
      this.initComponent();
    } else {
      this.servicesLoadedEventSubscriber = this.loadingService.servicesLoadedEvent$.subscribe(() => {
        if (this.$landmarksById()) {
          this.initComponent();
        } else {
          this.poisLoadedEventSubscriber = this.loadingService.poisLoadedEvent$.subscribe(() => {
            this.initComponent();
            this.poisLoadedEventSubscriber.unsubscribe();
          });
        }
      });
    }
  }

  private initComponent() {
    this.device = this._devicesManagerService.get(this.d);
  }

  ngOnDestroy() {
    this.mapService.$realClosest.set({});
    if (this.servicesLoadedEventSubscriber) {
      this.servicesLoadedEventSubscriber.unsubscribe();
    }
  }

  public goTo(uid: number) {
    // La route détail attend la vue dans l'URL : reprendre celle en cours quand il y
    // en a une (clic depuis le parc), sinon la carte — les onglets qui hébergent la
    // card (dashboard, widgets, alarmes…) n'ont pas de param `view` à préserver.
    this.router.navigate([DEVICES_UNIT_DEVICE(this.device.uid_parent, uid)], {
      queryParams: { view: this._viewService.currentView() ?? 'map' },
      queryParamsHandling: 'merge',
    });
  }
}
